export type TuitionWorkflowSession = {
  candidatesRequested?: boolean;
  calculationStarted?: boolean;
  studentCount?: number;
  postAccepted?: boolean;
};

const WORKFLOW_STORAGE_PREFIX = "tuition-workflow:";

function storageKey(cycleId: number): string {
  return `${WORKFLOW_STORAGE_PREFIX}${cycleId}`;
}

export function shouldFetchCurrentCalculationCycle(
  isAdminDashboard: boolean,
  activeCalculationCycleId: number | null,
): boolean {
  return isAdminDashboard && activeCalculationCycleId != null;
}

export function findActiveTuitionCalculationCycleId(): number | null {
  if (typeof window === "undefined") return null;

  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (!key?.startsWith(WORKFLOW_STORAGE_PREFIX)) continue;
    const cycleId = Number(key.slice(WORKFLOW_STORAGE_PREFIX.length));
    if (!Number.isFinite(cycleId) || cycleId <= 0) continue;
    if (readTuitionWorkflowSession(cycleId)?.calculationStarted) {
      return cycleId;
    }
    if (readTuitionWorkflowSession(cycleId)?.postAccepted) {
      return cycleId;
    }
  }

  return null;
}

function notifySessionChange(cycleId: number): void {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent("tuition-workflow-session-change", {
      detail: { cycleId },
    }),
  );
}

export function readTuitionWorkflowSessionRaw(cycleId: number): string | null {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(storageKey(cycleId));
}

export function readTuitionWorkflowSession(
  cycleId: number,
): TuitionWorkflowSession | null {
  if (typeof window === "undefined") return null;

  try {
    const raw = sessionStorage.getItem(storageKey(cycleId));
    if (!raw) return null;
    return JSON.parse(raw) as TuitionWorkflowSession;
  } catch {
    return null;
  }
}

export function writeTuitionWorkflowSession(
  cycleId: number,
  patch: TuitionWorkflowSession,
): void {
  if (typeof window === "undefined") return;

  const current = readTuitionWorkflowSession(cycleId) ?? {};
  sessionStorage.setItem(
    storageKey(cycleId),
    JSON.stringify({ ...current, ...patch }),
  );
  notifySessionChange(cycleId);
}

export function clearTuitionWorkflowSession(cycleId: number): void {
  if (typeof window === "undefined") return;
  sessionStorage.removeItem(storageKey(cycleId));
  notifySessionChange(cycleId);
}
